import AppSync from "@/services/AppSync"
import Broadcast from "@/types/Broadcast.interface"
import Message from "@/types/Message.interface"

interface State {
    broadcasts: Broadcast[]
}

const defaults = () => {
    return {
        broadcasts: [],
    }
}

export default {
    state: defaults(),
    mutations: {
        broadcastsPush (state: State, broadcasts: Broadcast[]): void {
            state.broadcasts = window.removeDuplicatesById(state.broadcasts, broadcasts)
        },
        broadcastsRemove (state: State, ids: number[]): void {
            //  Get our current list of broadcasts
            const broadcasts = state.broadcasts

            //  If no broadcasts, do nothing
            if (!broadcasts) return

            //  Else, filter out the removed ids
            const filtered = broadcasts.filter((x: Broadcast) => !ids.includes(x.id))

            //  Replace our broadcasts
            state.broadcasts = filtered
        },
        broadcastsReset(state) {
            Object.assign(state, defaults())
        }
    },
    actions: {
        broadcastsDelete ({ commit, getters }, broadcastId: number): Promise<Broadcast> {
            //  Assemble the API endpoint
            const url = `${getters.api}/broadcasts/${broadcastId}`

            //  Send the request
            return window.axios.delete(url).then(response => {
                //  Remove from our global list
                commit('broadcastsRemove', [response.data.id])

                //  Remove from our broadcasts view
                commit('broadcastsViewBroadcastsRemove', [response.data.id])

                //  Return the deleted broadcast
                return response.data
            }).catch(() => null)
        },
        broadcastsFind ({ getters }, broadcastId: number): Promise<Broadcast> {
            //  Assemble the API endpoint
            const url = `${getters.api}/broadcasts/${broadcastId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Return our found broadcast
                return response.data
            })
        },
        broadcastsMessages ({ getters }, broadcastId: number): Promise<Message[]> {
            //  Assemble the API endpoint
            const url = `${getters.api}/broadcasts/messages/${broadcastId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                return response.data
            }).catch(() => null)
        },
        broadcastsPost ({ dispatch, getters }, broadcast: Broadcast): Promise<Broadcast> {
            //  Get the current subscription
            const sub = getters.subscription

            //  If the user is past due then don't allow
            if (sub.status === 'past_due') {
                //  Pop a dialog informing the user
                dispatch('dialogPastDue')
                //  Return null because this failed
                return null
            }

            //  Assemble the API endpoint
            const url = `${getters.api}/broadcasts`

            //  Send the request
            return window.axios.post(url, broadcast).then(response => {
                //  Return our newly created broadcast (app sync should take care of updating our lists)
                return response.data
            }).catch(() => null)
        },
        broadcastsPersons ({ getters }, broadcast: Broadcast): Promise<number> {
            //  Assemble the API endpoint
            const url = `${getters.api}/broadcasts/persons`

            //  Send the request
            return window.axios.post(url, broadcast).then(response => {
                //  Return our total number of persons
                return response.data
            }).catch(() => null)
        },
        broadcastsPut ({ getters }, broadcast: Broadcast): Promise<Broadcast> {
            //  Assemble our API endpoint
            const url = `${getters.api}/broadcasts`

            //  Send the request
            return window.axios.put(url, broadcast).then(response => {
                //  Return our newly updated broadcast (app sync should take care of updating our lists)
                return response.data
            }).catch(() => null)
        },
        broadcastsWhereIn ({ getters }, broadcastIds: number[]): Promise<Broadcast[]> {
            //  Construct the payload
            const payload = { broadcast_ids: broadcastIds }
            
            //  Assemble our API endpoint
            const url = `${getters.api}/broadcasts/wherein`

            //  Send the request
            return window.axios.post(url, payload).then(response => {
                //  Return the found broadcasts
                return response.data
            })
        },
        //  Responsibile for getting draft properties such as groups and excluded groups
        draftGet ({ getters }, broadcastId: number): Promise<Broadcast> {
            
            //  Assemble our API endpoint
            const url = `${getters.api}/broadcasts/draft/${broadcastId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Return our draft properties
                return response.data
            }).catch(() => null)
        },
        draftPost ({ getters }, broadcast: Broadcast): Promise<Broadcast> {
            //  Assemble the API endpoint
            const url = `${getters.api}/broadcasts/draft`

            //  Send the request
            return window.axios.post(url, broadcast).then(response => {
                //  Faster than app sync for the user who posted
                AppSync.onBroadcastsPosted([response.data])

                //  Return our newly created draft broadcast (app sync should take care of updating our lists)
                return response.data
            }).catch(() => null)
        },
        draftPut({ getters }, broadcast: Broadcast): Promise<Broadcast> {
            //  Assemble our API endpoint
            const url = `${getters.api}/broadcasts/draft`

            //  Send the request
            return window.axios.put(url, broadcast).then(response => {
                //  Faster than app sync for the user who posted
                AppSync.onBroadcastsPosted([response.data])

                //  Return our newly updated broadcast draft (app sync should take care of updating our lists)
                return response.data
            }).catch(() => null)
        }
    },
    getters: {
        broadcasts (state: State): Broadcast[] {
            return state.broadcasts
        }
    }
}